import { useState } from 'react';
import { Search, RefreshCw, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';

function PrinterConfiguration({ 
    printerEnabled, 
    setPrinterEnabled, 
    setCashDrawerEnabled, 
    onPrinterDataChange 
}) {
    // Printer scanning state
    const [isSearchingPrinters, setIsSearchingPrinters] = useState(false);
    const [foundPrinters, setFoundPrinters] = useState([]);
    const [selectedPrinter, setSelectedPrinter] = useState(null);
    const [scanProgress, setScanProgress] = useState({ scanned: 0, total: 0, percentage: 0, timeRemaining: 0 });
    const [scanStartTime, setScanStartTime] = useState(null);
    const [globalController, setGlobalController] = useState(null);

    // Manual IP entry state
    const [manualIP, setManualIP] = useState('');
    const [showManualEntry, setShowManualEntry] = useState(false);

    // Test single printer connection with 3-minute timeout
    const testSinglePrinter = async (ip, controller) => {
        try {
            // Test ePOS-Print capability on port 80 (discovery port)
            await fetch(`http://${ip}/epos/capability.xml`, {
                method: 'GET',
                mode: 'no-cors',
                signal: controller.signal
            });
            // Return port 8008 for storage (actual ePOS SDK connection port)
            return { success: true, ip, port: 8008, method: 'epos-print' };
        } catch (eposError) {
            if (eposError.name === 'AbortError') {
                throw new Error('Timeout');
            }
            
            // If ePOS fails, try port 9100 (raw printing)
            try {
                await fetch(`http://${ip}:9100`, {
                    method: 'GET',
                    mode: 'no-cors',
                    signal: controller.signal
                });
                return { success: true, ip, port: 9100, method: 'raw-9100' };
            } catch (rawError) {
                throw new Error('No printer response');
            }
        }
    };

    // Add manual IP to printer list
    const addManualPrinter = async () => {
        if (!manualIP.trim()) {
            toast.error('Please enter an IP address');
            return;
        }

        // Basic IP validation
        const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
        if (!ipRegex.test(manualIP.trim())) {
            toast.error('Please enter a valid IP address');
            return;
        }

        // Check if IP already exists
        const existingPrinter = foundPrinters.find(p => p.ip === manualIP.trim());
        if (existingPrinter) {
            toast.error('This IP address is already in the list');
            return;
        }

        setIsSearchingPrinters(true);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);

        try {
            toast.loading(`Testing connection to ${manualIP}...`, { id: 'manual-test' });
            
            const result = await testSinglePrinter(manualIP.trim(), controller);
            
            if (result.success) {
                const printerData = {
                    ...result,
                    ip: manualIP.trim(),
                    port: result.port,
                    method: result.method
                };

                // Add to found printers and auto-select
                const updatedPrinters = [...foundPrinters, printerData];
                setFoundPrinters(updatedPrinters);
                setSelectedPrinter(printerData);
                
                // Notify parent component
                if (onPrinterDataChange) {
                    onPrinterDataChange({
                        selectedPrinter: printerData,
                        foundPrinters: updatedPrinters,
                        printerEnabled: printerEnabled
                    });
                }

                toast.success(`✅ Printer added: ${manualIP} (${result.method})`, { id: 'manual-test' });
                
                // Clear input and hide manual entry
                setManualIP('');
                setShowManualEntry(false);
            } else {
                toast.error(`❌ No printer found at ${manualIP}`, { id: 'manual-test' });
            }
        } catch (error) {
            if (error.message === 'Timeout') {
                toast.error(`⏱️ Connection timeout - ${manualIP} is not reachable`, { id: 'manual-test' });
            } else {
                toast.error(`❌ Connection failed: ${error.message}`, { id: 'manual-test' });
            }
        } finally {
            clearTimeout(timeout);
            setIsSearchingPrinters(false);
        }
    };

    // Manual stop scan function
    const stopScan = () => {
        if (globalController) {
            globalController.abort();
            console.log('🛑 Manual scan stop requested');
            toast('Scan stopped manually', {
                icon: '⏹️',
                style: {
                    borderRadius: '10px',
                    background: '#333',
                    color: '#fff',
                },
            });
        }
        setIsSearchingPrinters(false);
        setGlobalController(null);
    };

    // Single parallel execution with 3-minute timeout
    const scanForPrinters = async () => {
        setIsSearchingPrinters(true);
        setFoundPrinters([]);
        const startTime = Date.now();
        setScanStartTime(startTime);
        
        // Generate all IP addresses to scan
        const allIps = [];
        
        // 192.168.1.2 to 192.168.1.255
        for (let i = 2; i <= 255; i++) {
            allIps.push(`192.168.1.${i}`);
        }
        
        // 192.168.0.2 to 192.168.0.255
        for (let i = 2; i <= 255; i++) {
            allIps.push(`192.168.0.${i}`);
        }
        
        // Removed 10.0.0.x range per user request - don't scan these IPs

        const totalToScan = allIps.length;
        const scanDuration = 3 * 60 * 1000; // 3 minutes in milliseconds
        
        setScanProgress({ scanned: 0, total: totalToScan, percentage: 0, timeRemaining: 180 });
        console.log(`Executing all ${totalToScan} IP addresses in parallel with 3-minute timeout...`);
        toast.success(`Testing all ${totalToScan} IPs in parallel - 3min timeout`);
        
        // Global abort controller for 3-minute timeout
        const controller = new AbortController();
        setGlobalController(controller);
        const foundPrintersMap = new Map();
        let completedCount = 0;
        
        // Set global 3-minute timeout
        const globalTimeout = setTimeout(() => {
            controller.abort();
            console.log('⏰ Global 3-minute timeout reached - cancelling remaining requests');
        }, scanDuration);
        
        // Update timer every second
        const timerInterval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, Math.ceil((scanDuration - elapsed) / 1000));
            
            setScanProgress(prev => ({
                ...prev,
                timeRemaining: remaining
            }));
            
            if (remaining === 0) {
                clearInterval(timerInterval);
            }
        }, 1000);
        
        try {
            // Create all printer test promises at once
            const printerPromises = allIps.map(async (ip) => {
                try {
                    const result = await testSinglePrinter(ip, controller);
                    
                    // Immediately add to found printers if successful
                    if (result.success && !foundPrintersMap.has(ip)) {
                        const printer = { ...result, ip };
                        foundPrintersMap.set(ip, printer);
                        
                        // Update UI immediately
                        setFoundPrinters(Array.from(foundPrintersMap.values()));
                        
                        console.log(`✅ Printer found: ${ip} (${result.method})`);
                        toast.success(`Printer discovered: ${ip}`);
                    }
                    
                    return result;
                } catch (error) {
                    return { success: false, ip, error: error.message };
                } finally {
                    // Update progress
                    completedCount++;
                    setScanProgress(prev => ({
                        ...prev,
                        scanned: completedCount,
                        percentage: Math.round((completedCount / totalToScan) * 100)
                    }));
                }
            });
            
            console.log(`🚀 Started ${totalToScan} parallel requests...`);
            
            // Wait for all promises to complete or timeout
            await Promise.allSettled(printerPromises);
            
            // Clear timeout since we completed naturally
            clearTimeout(globalTimeout);
            
            const endTime = Date.now();
            const scanDurationTime = ((endTime - startTime) / 1000).toFixed(1);
            
            // Process final results
            const finalFoundPrinters = Array.from(foundPrintersMap.values());
            const responsiveIps = finalFoundPrinters.map(p => p.ip);
            
            console.log(`✅ Parallel scan completed in ${scanDurationTime}s`);
            console.log(`Found ${finalFoundPrinters.length} printers:`, finalFoundPrinters);
            console.log('📄 RESPONSIVE IPs:', responsiveIps.join(', '));
            
            // Show completion message
            if (finalFoundPrinters.length > 0) {
                toast.success(`Scan completed! Found ${finalFoundPrinters.length} printer(s) in ${scanDurationTime}s`);
            } else {
                toast(`Scan completed in ${scanDurationTime}s - no printers found`, {
                    icon: '⚠️',
                    style: {
                        borderRadius: '10px',
                        background: '#fbbf24',
                        color: '#fff',
                    },
                });
            }
            
        } catch (error) {
            console.error('Scan error:', error);
            toast.error(`Scan error: ${error.message}`);
        } finally {
            clearTimeout(globalTimeout);
            clearInterval(timerInterval);
            setIsSearchingPrinters(false);
            setGlobalController(null);
            setScanProgress({ scanned: totalToScan, total: totalToScan, percentage: 100, timeRemaining: 0 });
        }
    };

    // Enable printer and auto-scan
    const handleEnablePrinter = async () => {
        setPrinterEnabled(true);
        toast.success('Printer enabled! Scanning network for printers...');
        await scanForPrinters();
    };

    // Select found printer
    const handleSelectPrinter = (printer) => {
        setSelectedPrinter(printer);
        toast.success(`Printer selected: ${printer.ip}`);
        
        // Notify parent component about printer data change
        if (onPrinterDataChange) {
            onPrinterDataChange({
                selectedPrinter: printer,
                foundPrinters: foundPrinters,
                printerEnabled: printerEnabled
            });
        }
    };

    // Handle printer disable
    const handleDisablePrinter = () => {
        setPrinterEnabled(false);
        setCashDrawerEnabled(false);
        setFoundPrinters([]);
        setSelectedPrinter(null);
        
        // Notify parent component
        if (onPrinterDataChange) {
            onPrinterDataChange({
                selectedPrinter: null,
                foundPrinters: [],
                printerEnabled: false
            });
        }
    };

    // Handle toggle click
    const handleToggleClick = () => {
        if (printerEnabled) {
            handleDisablePrinter();
        } else {
            handleEnablePrinter();
        }
    };

    return (
        <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                    <h4 className="font-medium text-gray-900">🖨️ Printer Settings</h4>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                        Required First
                    </span>
                    {/* Toggle Switch */}
                    <div className="flex items-center">
                        <button
                            onClick={handleToggleClick}
                            disabled={isSearchingPrinters}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                                printerEnabled ? 'bg-green-600' : 'bg-gray-200'
                            } ${isSearchingPrinters ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                    printerEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                        <span className={`ml-2 text-sm font-medium ${
                            printerEnabled ? 'text-green-600' : 'text-gray-500'
                        }`}>
                            {isSearchingPrinters ? 'Scanning...' : printerEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                    </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center space-x-2">
                    {!printerEnabled && (
                        <button 
                            onClick={handleEnablePrinter}
                            disabled={isSearchingPrinters}
                            className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                        >
                            {isSearchingPrinters ? (
                                <>
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                    <span>Parallel Scan...</span>
                                </>
                            ) : (
                                <>
                                    <Search className="h-4 w-4" />
                                    <span>Start Parallel Scan</span>
                                </>
                            )}
                        </button>
                    )}
                    
                    {printerEnabled && !isSearchingPrinters && (
                        <>
                            <button 
                                onClick={scanForPrinters}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors flex items-center space-x-1"
                            >
                                <Search className="h-3 w-3" />
                                <span>Scan Network</span>
                            </button>
                            <button 
                                onClick={() => setShowManualEntry(!showManualEntry)}
                                className="px-3 py-1 bg-purple-100 text-purple-700 rounded text-sm hover:bg-purple-200 transition-colors flex items-center space-x-1"
                            >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                <span>Add Manual IP</span>
                            </button>
                        </>
                    )}
                    
                    {printerEnabled && isSearchingPrinters && (
                        <button 
                            onClick={stopScan}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors flex items-center space-x-1"
                        >
                            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                            </svg>
                            <span>Stop Scan</span>
                        </button>
                    )}
                    
                    <button 
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                            printerEnabled && selectedPrinter
                                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        }`}
                        disabled={!printerEnabled || !selectedPrinter}
                    >
                        Setup
                    </button>
                </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-3">
                Click "Scan Network" to search for printers on your network. Found devices will appear in real-time. Use "Stop Scan" to manually stop scanning.
            </p>

            {/* Manual IP Entry Section */}
            {printerEnabled && showManualEntry && !isSearchingPrinters && (
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200 mb-3">
                    <h5 className="text-sm font-medium text-purple-800 mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Printer Manually
                    </h5>
                    <p className="text-xs text-purple-600 mb-3">
                        Enter the IP address of your printer if it wasn't found during scanning.
                    </p>
                    <div className="flex items-center space-x-2">
                        <div className="flex-1">
                            <input
                                type="text"
                                value={manualIP}
                                onChange={(e) => setManualIP(e.target.value)}
                                placeholder="e.g., 192.168.1.100"
                                className="w-full px-3 py-2 text-sm border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                onKeyDown={(e) => e.key === 'Enter' && addManualPrinter()}
                            />
                        </div>
                        <button
                            onClick={addManualPrinter}
                            disabled={!manualIP.trim() || isSearchingPrinters}
                            className="px-3 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <span>Test & Add</span>
                        </button>
                        <button
                            onClick={() => setShowManualEntry(false)}
                            className="px-2 py-2 bg-gray-100 text-gray-600 text-sm rounded hover:bg-gray-200 transition-colors"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div className="mt-2 text-xs text-purple-600">
                        <div className="flex items-center space-x-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <span>The system will test the connection and automatically detect the best protocol (ePOS-Print or Raw).</span>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Scanning Progress */}
            {isSearchingPrinters && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 mb-3">
                    <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-blue-700 font-medium">Scanning Network...</span>
                        <div className="flex items-center space-x-2">
                            <span className="text-blue-600">{scanProgress.percentage}%</span>
                            <span className="text-blue-800 font-mono text-xs bg-blue-100 px-2 py-1 rounded">
                                {Math.floor(scanProgress.timeRemaining / 60)}:{(scanProgress.timeRemaining % 60).toString().padStart(2, '0')}
                            </span>
                        </div>
                    </div>
                    <div className="bg-blue-200 rounded-full h-2 mb-2">
                        <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${scanProgress.percentage}%` }}
                        ></div>
                    </div>
                    <div className="bg-orange-200 rounded-full h-1">
                        <div 
                            className="bg-orange-600 h-1 rounded-full transition-all duration-1000"
                            style={{ width: `${100 - ((scanProgress.timeRemaining / 180) * 100)}%` }}
                        ></div>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                        Found {foundPrinters.length} printer(s) • Tested {scanProgress.scanned}/{scanProgress.total} IPs
                    </p>
                </div>
            )}

            {/* Real-time Found Devices During Scanning */}
            {isSearchingPrinters && foundPrinters.length > 0 && (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200 mb-3">
                    <h5 className="text-sm font-medium text-green-800 mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Found Devices (Live):
                    </h5>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                        {foundPrinters.map((printer, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-2 bg-white border border-green-300 rounded text-xs"
                            >
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                    <span className="font-medium">{printer.ip}</span>
                                    <span className="text-green-600 bg-green-100 px-1 py-0.5 rounded">
                                        {printer.method}
                                    </span>
                                </div>
                                <button
                                    onClick={() => handleSelectPrinter(printer)}
                                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                                >
                                    Select
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {/* Found Printers List */}
            {foundPrinters.length > 0 && !isSearchingPrinters && (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200 mb-3">
                    <h5 className="text-sm font-medium text-green-800 mb-2">
                        Found {foundPrinters.length} printer(s) - Select one:
                    </h5>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                        {foundPrinters.map((printer, index) => (
                            <button
                                key={index}
                                onClick={() => handleSelectPrinter(printer)}
                                className={`w-full text-left p-2 border rounded transition-colors ${
                                    selectedPrinter?.ip === printer.ip
                                        ? 'border-green-500 bg-green-100'
                                        : 'border-green-300 bg-white hover:bg-green-50'
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <MapPin className="h-3 w-3 text-green-600" />
                                        <span className="font-medium text-sm">{printer.ip}</span>
                                        {selectedPrinter?.ip === printer.ip && (
                                            <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">Selected</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-green-600">
                                        {printer.method}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Printer Status & Settings */}
            {printerEnabled && selectedPrinter && (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">Printer Status:</span>
                            <span className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                <span className="text-green-600 font-medium">Ready</span>
                            </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">Selected Printer:</span>
                            <span className="text-gray-900 font-mono text-xs">{selectedPrinter.ip}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">Connection Type:</span>
                            <span className="text-green-600 font-medium">{selectedPrinter.method}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">Auto-cut:</span>
                            <span className="text-green-600 font-medium">Enabled</span>
                        </div>
                    </div>
                </div>
            )}

            {printerEnabled && foundPrinters.length === 0 && !isSearchingPrinters && (
                <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                    <p className="text-sm text-yellow-700 text-center">
                        No printers found. Make sure your printer is connected to the network and try scanning again.
                    </p>
                </div>
            )}
            
            {!printerEnabled && (
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-sm text-gray-500 text-center">
                        Enable printer to access thermal printing and cash drawer features
                    </p>
                </div>
            )}
        </div>
    );
}

export default PrinterConfiguration;